<script setup lang="ts">
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useMinerRepairActions, type UpdateRepairActionMutation } from '@/composables/rc/miner-repair-actions'
import { usePartDetails } from '@/composables/rc/part-details'
import { useRepairActions } from '@/composables/rc/repair-actions'
import { Icon } from '@iconify/vue'
import { computed, ref, watchEffect } from 'vue'

// Note: old 'repair actions' is now labeled 'repair charges'

const { minerId, selectedPart } = useRepairsContext()
const { partType, repairCharges, hasRepairCharges, failureId, isReplacement, movedToInventory } = usePartDetails(
  minerId,
  selectedPart
)

const selectedRepairCharges = ref()

// Sync UI state to BE src
watchEffect(() => {
  selectedRepairCharges.value = repairCharges.value
})

const editing = ref(false)

const { updateFailureMutation, deleteFailureRepairActionMutation } = useMinerRepairActions(minerId)

const { getRepairActionsOptions } = useRepairActions()
const repairChargesOptions = computed(() => getRepairActionsOptions(partType))

/**
 * Updates a failure.
 *
 * @param {UpdateRepairActionMutation} param0 updateRepairActionMutation params.
 */
const updateFailure = ({ failureTypeId, repairChargeId, failureReason }: UpdateRepairActionMutation) => {
  if (!failureId.value) return
  updateFailureMutation.mutate(
    {
      failureId: failureId.value,
      partName: selectedPart.value,
      failure_type_id: failureTypeId,
      ...(repairChargeId && { executed_repair_action_id: repairChargeId }),
      comment: failureReason,
    },
    {
      onSettled: () => {
        if (editing.value) editing.value = false
      },
    }
  )
}

const deleteRepairCharge = () => {
  if (!failureId.value) return
  deleteFailureRepairActionMutation.mutate(
    { partName: selectedPart.value, failureId: failureId.value },
    {
      onError: () => {
        selectedRepairCharges.value = repairCharges.value
      },
      onSettled: () => {
        if (editing.value) editing.value = false
      },
    }
  )
}

const onSetRepairCharge = () => {
  if (!selectedRepairCharges.value) deleteRepairCharge()
  else updateFailure({ repairChargeId: selectedRepairCharges.value.id })
}
</script>

<template>
  <RepairSection title="Repair Charges" :step="3" :active="hasRepairCharges">
    <div class="w-full flex items-center gap-2">
      <el-select
        v-model="selectedRepairCharges"
        value-key="id"
        :disabled="(hasRepairCharges && (!editing || isReplacement)) || movedToInventory"
        clearable
        @change="onSetRepairCharge"
      >
        <el-option
          v-for="charge in repairChargesOptions"
          :key="charge.value.id"
          :value="charge.value"
          :label="charge.label"
        >
          {{ charge.label }}
        </el-option>
      </el-select>
      <Icon
        v-if="hasRepairCharges && !editing && !isReplacement && !movedToInventory"
        class="cursor-pointer"
        icon="material-symbols:edit-outline"
        @click="editing = true"
      />
      <Icon
        v-if="hasRepairCharges && editing"
        class="cursor-pointer"
        icon="material-symbols:check-rounded"
        @click="editing = false"
      />
    </div>
  </RepairSection>
</template>
